"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimationProvider } from "@/contexts/AnimationContext";
import { Toaster } from "@/components/ui/toaster";
import Navigation from "@/components/Navigation";
import ContactNavigator from "@/components/ContactNavigator";
import HashScroller from "@/components/HashScroller";
import ScrollDepthTracker from "@/components/ScrollDepthTracker";
import { useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { getConsent } from "@/lib/consent";

export function Providers({ children }: { children: React.ReactNode }) {
  // Disable browser scroll restoration to prevent scroll jump on reload
  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    // Keep the browser's native anchor scroll when a hash is present
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    // Only initialize PostHog in production, and not for users who previously rejected consent
    if (
      process.env.NODE_ENV === "production" &&
      process.env.NEXT_PUBLIC_POSTHOG_KEY &&
      getConsent() !== "rejected"
    ) {
      try {
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
          api_host:
            process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
          person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
          defaults: "2025-05-24", // Required for exception autocapture
        });
      } catch (error) {
        console.error("Failed to initialize PostHog:", error);
      }
    }

    // Global unhandled rejection handler for debugging
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", {
        reason: event.reason,
        promise: event.promise,
      });
      // Send to PostHog for tracking with full context
      if (typeof posthog !== "undefined" && posthog.__loaded) {
        // Try to capture as exception if it's an Error object
        if (event.reason instanceof Error) {
          posthog.captureException(event.reason, {
            tags: { type: "unhandled_rejection" },
          });
        } else {
          // Fallback for non-Error rejections
          posthog.capture("unhandled_rejection", {
            error: event.reason?.toString() || "Unknown error",
            stack: event.reason?.stack || "No stack trace",
            url: window.location.href,
            timestamp: new Date().toISOString(),
          });
        }
      }
    };

    // Global error handler for catching uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error("Uncaught error:", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
      // Send to PostHog with full stack trace
      if (typeof posthog !== "undefined" && posthog.__loaded && event.error) {
        posthog.captureException(event.error, {
          tags: { type: "uncaught_error" },
          extra: {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            url: window.location.href,
          },
        });
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
      window.removeEventListener("error", handleError);
    };
  }, []);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      }),
  );

  // Conditionally wrap with PostHog provider only in production
  const content = (
    <QueryClientProvider client={queryClient}>
      <AnimationProvider>
        <ContactNavigator />
        <HashScroller />
        <ScrollDepthTracker />
        {/* <Navigation /> */}
        {children}
        <Toaster />
      </AnimationProvider>
    </QueryClientProvider>
  );

  // Only wrap with PostHog provider in production
  if (
    process.env.NODE_ENV === "production" &&
    process.env.NEXT_PUBLIC_POSTHOG_KEY
  ) {
    return <PHProvider client={posthog}>{content}</PHProvider>;
  }

  return content;
}
